import * as React from 'react';
import {
    AdServerTagSideEnum,
    ITagMetrics,
    IAdServerTagTotals,
    IFrontendAdServerTag,
    IAdServerTag
} from "@leap4/entities-discrepancy-common";
import {DynamicDatagrid, IDataGridProps, MuiDataGrid, Paper, Checkbox} from "@leap4/frontend";

import {EntitiesProperties} from "../../../../../utils/enums";
import {IAdServerConnectedTags} from "../interfaces";
import {Types} from "mongoose";
import {IPublisherTagData} from "../../../publisherTags/PublisherTag/ConnectedTagsTable/publisherConnectedTag";
import {useContext, useEffect, useState} from "react";
import {UserViewsContext} from "../../../../../contexts/userViewsContext";
import {generateColumn} from "../../../common/tableColumnsGenerator";
type GridColDef = MuiDataGrid.GridColDef;

const CheckboxTableCell = ({handleChange}: { handleChange: Function }) => {
    const [checked, setChecked] = React.useState(false);
    const handleChangeHook = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
        handleChange(event.target.checked);
    };
    return <Checkbox
        checked={checked}
        onChange={handleChangeHook}
        inputProps={{'aria-label': 'controlled'}}
    />
}

export type connectedAdServerTag = {
    tag: IFrontendAdServerTag,
    totals: IAdServerTagTotals,
}

export interface IAdServerRowConnectedTag {
    id: string | Types.ObjectId
    name: string
    campaignId: string
    revenue: number,
    cost: number
    impressions: number,
    requests: number,
    dataPoint: string | Types.ObjectId
    profit: number,
    profitPercent: number,
    fillRate: number,
    cpm: number,
    rpm: number,
    vcr: number
}

export interface IItems {
    tags: IAdServerConnectedTags;
    publisherTags: IPublisherTagData[];
}

export interface IAdServerTagsTableProps extends Partial<IDataGridProps> {
    items: IItems,
    loading: boolean,
    side: AdServerTagSideEnum,
    onConnectedTagCheckboxClick: (connectedTag: IAdServerRowConnectedTag, isChecked: boolean) => void
    compareData: IAdServerConnectedTags
}

export default function ConnectedTagsTable({
                                               items,
                                               loading,
                                               refreshFunc,
                                               compareData
                                           }: IAdServerTagsTableProps) {
    const {userViews} = useContext(UserViewsContext)
    const align = 'left'
    const {
        NAME, CAMPAIGN_ID, PUBLISHER_IMPRESSIONS, IMPRESSIONS_DIFFERENCE, PUBLISHER_REVENUE, REVENUE_DIFFERENCE
    } = EntitiesProperties.NOT_NUMERIC
    const [columns, setColumns] = useState<GridColDef[]>([])
    useEffect(() => {
        const userPresetColumns = userViews.activePreset.tableViews.map(view => generateColumn(view, compareData))
        const preConstantColumns = [NAME, CAMPAIGN_ID].map(view => generateColumn(view, compareData))
        let prePublisherConstantColumns  = []
        if(items.publisherTags && items.publisherTags.length > 0){
            prePublisherConstantColumns = [PUBLISHER_IMPRESSIONS, IMPRESSIONS_DIFFERENCE, PUBLISHER_REVENUE, REVENUE_DIFFERENCE].map(view => generateColumn(view, compareData))
        }
        setColumns([...preConstantColumns, ...prePublisherConstantColumns, ...userPresetColumns, ])
    }, [userViews, compareData])

    function createRowData(item: { tag: IAdServerTag; totals: ITagMetrics } | IPublisherTagData): IAdServerRowConnectedTag {
        const {totals} = item as { tag: IAdServerTag; totals: ITagMetrics };
        const itemConnectedTag = item as { tag: IAdServerTag; totals: ITagMetrics };
        const profit = totals.revenue - totals.cost;
        const baseObj = {
            id: itemConnectedTag.tag._id,
            name: itemConnectedTag.tag.name,
            campaignId: itemConnectedTag.tag.campaignId,
            cost: totals.cost,
            revenue: totals.revenue,
            impressions: totals.impressions,
            dataPoint: itemConnectedTag.tag.dataPoint._id,
            // adServer: item.tag.dataPoint.adServer.name,
            //metrics: item.metrics?.metrics,
            requests: totals.requests,
            profit,
            completion: totals.completion,
            profitPercent: (profit / totals.revenue) * 100,
            fillRate: ((totals?.impressions || 0) / (totals?.requests)) * 100,
            cpm: totals?.impressions === 0 ? 0 : (totals?.cost / totals?.impressions) * 1000,
            rpm: totals?.impressions === 0 ? 0 : (totals?.revenue / totals?.impressions) * 1000,
            vcr: totals?.impressions === 0 ? 0 : totals?.completion / totals?.impressions * 100,
            netProfit: totals?.netRevenue - totals?.netCost,
            ...totals
        }
        const publisherTagData = item as any
        if (publisherTagData.publisherRevenue && publisherTagData.publisherImpressions) {
            baseObj["publisherRevenue"] = publisherTagData.publisherRevenue;
            baseObj["publisherImpressions"] = publisherTagData.publisherImpressions;
            baseObj["revenueDifference"] = publisherTagData.publisherRevenue? (100* (publisherTagData.publisherRevenue - itemConnectedTag.totals.revenue)/ publisherTagData.publisherRevenue).toFixed(2): null
            baseObj["impressionsDifference"] = publisherTagData.publisherImpressions? (100* (publisherTagData.publisherImpressions - itemConnectedTag.totals.impressions)/ publisherTagData.publisherImpressions).toFixed(2):null
        }
        return baseObj;
    }

    const publisherTags = items.publisherTags as IPublisherTagData[]
    const rows = items.tags.map((row,) => {
            let fixedRow = row;
            if (publisherTags && publisherTags.length > 0) {
                const matchingPublisherTag = publisherTags.find(tag => tag.campaignId === row.tag.campaignId)
                fixedRow["publisherRevenue"] = matchingPublisherTag? matchingPublisherTag.totals.revenue: null;
                fixedRow["publisherImpressions"] = matchingPublisherTag? matchingPublisherTag.totals.impressions: null;
            }
            return createRowData(fixedRow)
    }

    ).sort((a, b) => b.revenue > a.revenue ? 1 : -1);

    return (
        <Paper sx={{margin: "20px 0", borderRadius: "16px", boxShadow: "none"}} elevation={5}>
            <DynamicDatagrid rows={rows}
                             columns={columns}
                             loading={loading}
                             loadError={false}
                             refreshFunc={refreshFunc}
                             pageSize={100}
                             style={{minHeight: `75vh`, width: '100%'}}
            />
        </Paper>
    );
}
